// Builder — emits the structured SlideData JSON for the chosen template.
//
// Two big differences from kula-create:
//   1. Output is SlideData JSON, not a hydrated HTML doc. The KulaLearn
//      renderer is a React component; the Builder's only job is to fill the
//      template's data contract.
//   2. All 17 interaction types have a registered DATA_CONTRACTS entry —
//      buildBuilderSystem throws if a type is ever added without one.

import {
  CHARTER,
  BUILDER_SYSTEM,
  NARRATIVE_WRAPPER,
} from '../prompts/bundle.js';
import type { BuilderInput } from './types.js';

// ---------------------------------------------------------------------------
// Data contracts injected into the Builder's system prompt per template.
// Each contract is the TypeScript snippet describing the exact shape the
// Builder must emit. New templates extend this map as Phase 4 lands them.
// ---------------------------------------------------------------------------
export const DATA_CONTRACTS: Record<string, string> = {
  'reveal-predict': `interface PredictData {
  title: string;
  subtitle: string;
  predictions: Array<{
    id: string;
    question: string;
    context?: string;
    options: Array<{ value: string; text: string }>;  // 2-4 options
    correctValue: string;
    revealAnswer: string;
    matchFeedback: string;
    missFeedback: string;
  }>;
  summary: { prompt: string };
}`,

  'confidence-meter': `interface ConfidenceData {
  title: string;
  subtitle: string;
  items: Array<{
    id: string;
    statement: string;          // The claim the learner rates
    content: string;            // 2-3 sentences revealed after pre-rating
    scale: { min: 1; max: 5; labels: [string, string, string] };  // [low, mid, high]
  }>;
  summary: { prompt: string };  // Reflection on what shifted
}
// 4-6 items per slide. The shift between pre and post is the learning signal —
// make sure the revealed content has real potential to move the rating.`,

  'drag-sort': `interface SortData {
  title: string;
  subtitle: string;
  instruction: string;
  mode: 'categorise' | 'sequence';
  items: Array<{
    id: string;
    text: string;
    correctZone: string;         // MUST match a zone id below
    correctPosition?: number;    // sequence mode only
  }>;                            // 4-8 items
  zones: Array<{ id: string; label: string }>;  // 2-4 zones
  feedback: {
    allCorrect: string;
    partial: string;
    perItem?: Record<string, string>;
  };
}
// CRITICAL: each item.correctZone must reference an existing zone id.
// Items should be distinguishable — don't make multiple items that obviously
// fit the same zone. Mix in some ambiguous-looking items that test
// understanding.`,

  'teach-back': `interface TeachBackData {
  title: string;
  subtitle: string;
  instruction: string;
  student: { name: string; role: string; voiceOpening: string };  // the struggling learner
  artifact: {                                    // their flawed work product
    kind: 'email' | 'message' | 'document' | 'code' | 'plan';
    header?: Array<{ label: string; value: string }>;
    fragments: Array<{
      id: string;
      text: string;
      strikeable: boolean;    // false = fixed connective text (greeting, sign-off)
      damage: boolean;        // true = a phrase the learner SHOULD strike
      loadBearing?: boolean;  // the ONE right line a careless reviewer wrongly cuts
    }>;                       // ≥1 damage fragment AND ≥1 loadBearing fragment
  };
  misconceptionId: string;    // MUST match a diagnosisOptions[].id
  diagnosisOptions: Array<{ id: string; text: string; correct: boolean; feedback: string }>;  // 1 correct + ≥2 plausible distractors
  coachingFragments: Array<{ id: string; text: string; kind: 'perspective' | 'targeted' | 'ownership' | 'backfire' | 'answer' }>;  // include ≥1 'backfire' + ≥1 'answer'
  maxCoaching: number;        // ≤3 fragments composed IN ORDER
  branches: Array<{           // order + inclusion of composed kinds picks the reply
    id: string;
    when: { opensWithKind?: string; includesAnyKind?: string[]; excludesAllKinds?: string[] };  // kinds from the coach-kind enum only
    outcome: 'aha' | 'partial' | 'backfire' | 'dependency';
    studentReply: string;     // the student's reply in their own voice
    coachNote: string;        // feedback to the learner on what their move did
    allowsAdapt?: boolean;    // backfire/dependency may offer one adapt beat
  }>;
  defaultBranch: { outcome: 'partial'; studentReply: string; coachNote: string };
  adaptBeat?: {
    prompt: string;
    options: Array<{ id: string; text: string; kind: string; leadsTo: 'aha' | 'unresolved'; studentReply: string; coachNote: string }>;
  };
  revision: {                 // beat 5 — the rewrite, improved but carrying ONE residue
    kind: 'email' | 'message' | 'document' | 'code' | 'plan';
    header?: Array<{ label: string; value: string }>;
    fragments: Array<{ id: string; text: string; strikeable: boolean; isResidue: boolean }>;  // EXACTLY one isResidue:true
  };
  expertNote: string;         // the expert teacher's contrasting move, shown at close
}
// CRITICAL: misconceptionId must match a diagnosisOptions id. The artifact needs
// at least one damage phrase AND one load-bearing phrase (over-striking is
// punished). Include at least one 'backfire' and one 'answer' coaching fragment
// so the wrong-move branches are reachable. The revision must carry exactly one
// residue fragment — the belief's fingerprint surviving the surface fix.`,

  'tension-mapper': `interface TensionMapperData {
  title: string;
  subtitle: string;
  tension: {
    statement: string;
    poleA: { label: string; description: string };
    poleB: { label: string; description: string };
  };
  factors: Array<{
    id: string;
    label: string;
    description: string;   // MAX 80 chars
  }>;                      // 6-10 factors
  expertAnalysis: {
    summary: string;
    insights: Array<{ factorId: string; perspective: string }>;  // one per factor
  };
  reflection: { prompt: string };
}
// CRITICAL: test every factor against both poles before including. Each
// factor should genuinely sit somewhere on the spectrum — none should
// trivially go to one pole. Expert insights must reference factor ids.`,

  'scenario-simulation': `interface ScenarioData {
  title: string;
  subtitle: string;
  // The artifact the scenario's decisions revolve around — the AI draft, the
  // email, the report the learner must weigh. REQUIRED whenever any branch's
  // situation/question/choice refers to one. Write the artifact ITSELF, in
  // full (the actual questions, the actual email text) — not a description of
  // it. It renders as a document at the entry branch.
  exhibit?: {
    kind: 'document' | 'draft' | 'email' | 'message' | 'report' | 'transcript' | 'data' | 'other';
    title: string;        // in-world title, e.g. "AI draft — discussion questions"
    body: string;         // the complete artifact text, every line a question refers to
    attribution?: string; // optional in-world source line
  };
  branches: Array<{
    id: string;            // CRITICAL: first branch MUST have id "start"
    type: 'decision' | 'ending';
    label?: string;        // decision only — short stage label
    situation?: string;    // decision only — context paragraph
    question?: string;     // decision only — what the learner must decide
    consequence?: string;  // decision only — result of the previous choice
    choices?: Array<{      // decision only — 2-3 choices
      letter: string;      // 'A' | 'B' | 'C'
      text: string;
      nextBranch: string;  // id of the next branch
      pathLabel: string;   // short label shown in path tracker
    }>;
    title?: string;        // ending only
    description?: string;  // ending only
    reflection?: string;   // ending only
  }>;
}
// CRITICAL: the first branch MUST have id "start" and type "decision".
// 3-5 decision branches + 2-3 ending branches. Every decision's choices must
// reference real branch ids via nextBranch. At least one ending should be
// reachable from "start" via every choice path.`,

  'timeline-navigator': `interface TimelineData {
  title: string;
  subtitle: string;
  events: Array<{
    id: string;
    date: string;              // e.g. "1492", "March 2020"
    title: string;
    summary: string;           // one-line preview
    detail: string;            // 2-4 sentences expanded
    isMilestone: boolean;      // highlights as a major event
    question?: string;         // optional in-line probe on this event
  }>;                          // 5-10 events, at least 2 milestones
  reflection: { prompt: string };
}
// CRITICAL: events must form a cause/effect arc, not a list. Detail should
// explain WHY this event mattered — not just WHAT happened.`,

  'progressive-builder': `interface BuilderData {
  title: string;
  subtitle: string;
  baseState: string;             // The starting picture before any layers
  layers: Array<{
    id: string;
    label: string;               // Short layer name
    addition: string;            // What this layer adds (shown as the "next step")
    transformedOutput: string;   // The CUMULATIVE result after this layer
  }>;                            // 3-5 layers
  reflection: { prompt: string };
}
// CRITICAL: transformedOutput is cumulative — it shows the full picture AFTER
// this layer, not just the layer's contribution. Each layer's output should
// visibly differ from the prior layer's.`,

  'guided-sandbox': `interface SandboxData {
  title: string;
  subtitle: string;
  task: string;                  // The open prompt
  placeholder: string;           // Textarea placeholder
  minScore: number;              // Passing threshold 0-100
  dimensions: Array<{
    id: string;
    label: string;
    description: string;
    keywords: string[];          // Substring matches — case-insensitive
    tips: string;                // Coaching shown after submit
    weight: number;              // Used in weighted overall score
  }>;                            // 3-5 dimensions
  guidance: Record<string, string>;  // Optional dimensionId → extra coaching
}
// CRITICAL: keywords are the scoring signal — pick concrete, specific
// phrases the learner would use if their answer landed the dimension.
// Generic keywords ("good", "correct") score everyone the same.`,

  'perspective-lenses': `interface PerspectiveLensesData {
  title: string;
  subtitle: string;
  stimulus: { context: string; imageAlt?: string };  // 80-150 words of SCENE-SETTING only
  // The artifact every lens re-reads — REQUIRED whenever any lens prompt asks
  // the learner to read, judge, or quote something (a draft, an email, a
  // report). Write the artifact ITSELF, in full — not a description of it.
  // stimulus.context sets the scene; exhibit.body IS the thing examined.
  exhibit?: {
    kind: 'document' | 'draft' | 'email' | 'message' | 'report' | 'transcript' | 'data' | 'other';
    title: string;        // in-world title, e.g. "AI draft — discussion questions"
    body: string;         // the complete artifact text, every line a lens refers to
    attribution?: string; // optional in-world source line
  };
  lenses: Array<{
    id: string;
    label: string;             // e.g. "Environmentalist", "Economist"
    iconKey: string;           // svg icon identifier (magnifying_glass | heart | warning | star | lightbulb | scale | users | shield | globe | target | book | compass | default)
    colour: string;            // hex accent for this lens
    prompt: string;            // main question for this lens
    subPrompts: Array<{
      id: string;
      text: string;
      inputType: 'text' | 'select';
      options?: string[];      // required for select
      maxLength?: number;
    }>;                        // 2-4 sub-prompts per lens
  }>;                          // 3-5 lenses, each must produce DIFFERENT responses
  synthesis: { prompt: string; showLensComparison: boolean };
}
// CRITICAL: lenses must produce genuinely different responses. Stimulus must
// include names, numbers, stakes — not generic. iconKey is a key from the
// library, NOT emoji.`,

  'scaffolded-investigation': `interface InvestigationData {
  title: string;
  subtitle: string;
  rounds: [
    {
      id: string;
      roundNumber: 1;
      mode: 'guided';
      title: string;
      content: string;
      readingPauseSeconds: number;
      presetProbes: Array<{ tag: string; question: string; response: string }>;  // 3-5 in round 1
      customProbesRequired: 0;
    },
    {
      id: string;
      roundNumber: 2;
      mode: 'semi-guided';
      title: string;
      content: string;
      readingPauseSeconds: number;
      presetProbes: Array<{ tag: string; question: string; response: string }>;  // 1-2 in round 2
      customProbesRequired: number;  // 1-2 in round 2
    },
    {
      id: string;
      roundNumber: 3;
      mode: 'independent';
      title: string;
      content: string;
      readingPauseSeconds: number;
      presetProbes: [];
      customProbesRequired: number;  // 3-5 in round 3
    }
  ];
  debrief: { prompt: string };
}
// CRITICAL: exactly 3 rounds; modes escalate guided → semi-guided →
// independent. Each round's content is different (not the same passage with
// different probes).`,

  'layered-reveal': `interface LayeredRevealData {
  title: string;
  subtitle: string;
  scenario: { context: string };
  layers: Array<{
    id: string;
    label: string;
    content: string;             // max 150 words per layer
    contentType: 'text' | 'data' | 'quote' | 'image-description';
    assessmentPrompts: Array<{
      id: string;
      text: string;
      inputType: 'text' | 'select' | 'scale';
      options?: string[];
      scaleLabels?: [string, string];
      maxLength?: number;
    }>;
  }>;                            // 3-5 layers
  synthesis: {
    revealText: string;          // The final reframe — why the layers add up to a different picture
    reflectionPrompt: string;
    showAssessmentTrail: boolean;
  };
}
// CRITICAL: each layer MUST shift the learner's assessment. Don't reuse the
// same prompt across layers — the new context demands a new judgement.
// Layer 1 should create a clear initial impression that later layers reframe.`,

  'claim-evidence': `interface ClaimEvidenceData {
  title: string;
  subtitle: string;
  claim: { statement: string; context?: string };
  evidencePool: Array<{
    id: string;
    label: string;              // 3-6 word card label
    detail: string;             // max 120 chars
    strength: 'strong' | 'partial' | 'distractor';  // hidden from learner
    expertReasoning?: string;   // shown after submit
  }>;                           // 6-10 pieces, at least 2 distractors
  argumentRules: { minEvidence: number; maxEvidence: number; requireReasoning: boolean };
  counterClaim?: { statement: string; strongEvidence: string[] };
  expertModel: { selectedEvidence: string[]; reasoning: string };  // answer key
  reflection: { prompt: string };
}
// CRITICAL: include at least 2 distractors. Strength must be defensible. Expert
// reasoning must connect the evidence to the claim explicitly.`,

  'gallery-walk': `interface GalleryData {
  title: string;
  subtitle: string;
  intro: string;
  stations: Array<{
    id: string;
    title: string;
    teaser: string;             // one-line preview
    content: Array<{ type: 'text' | 'image' | 'quote'; value: string; attribution?: string }>;
    reflectionPrompt: string;   // station-specific
  }>;                           // 4-6 stations
  synthesis: { prompt: string }; // cross-station reflection
}
// CRITICAL: stations are independent — each must stand alone. Reflection
// prompts must be station-specific, not interchangeable boilerplate.`,

  'spot-the-error': `interface ErrorData {
  title: string;
  subtitle: string;
  sections: Array<{
    id: string;
    instruction: string;   // e.g. "Read this AI-generated summary"
    context: string;       // what to look for (1-2 sentences)
    content: Array<
      | { type: 'text'; text: string }
      | { type: 'error'; text: string; errorId: string; explanation: string }
      | { type: 'decoy'; text: string }
    >;                     // ordered — renders inline as a single paragraph
    completionMessage: string;  // shown when all errors in this section are found
  }>;                      // 2-4 sections, each with 2-4 errors and 1-3 decoys
}
// CRITICAL: decoys are correct-but-suspicious-sounding. They test discrimination,
// not just suspicion. Errors must include a substantive expert explanation
// (not "this is wrong because it's not right").`,

  'before-after-bridge': `interface BeforeAfterBridgeData {
  title: string;
  subtitle: string;
  beforePhase: {
    instruction: string;
    prompts: Array<{
      id: string;                 // MUST match the after-phase prompt id
      beforeText: string;         // The pre-content question
      inputType: 'text' | 'scale';
      scaleLabels?: [string, string];  // required when inputType === 'scale'
      maxLength?: number;         // default 300
    }>;                           // 2-4 prompts
  };
  contentPhase: {
    instruction: string;
    blocks: Array<{
      id: string;
      type: 'text' | 'data' | 'quote';
      content: string;            // the actual reading material
      source?: string;
    }>;                           // 2-5 blocks
  };
  afterPhase: {
    instruction: string;
    prompts: Array<{
      id: string;                 // MUST match a beforePhase prompt id
      afterText: string;          // reworded version of the before question
    }>;
  };
  comparison: { reflectionPrompt: string };
}
// CRITICAL: every beforePhase prompt MUST have an afterPhase counterpart
// with the same id. The renderer pairs them on id to show the shift.
// Content blocks must include real assumption-challenging information —
// not generic background. The reframe is the point.`,

  'reflect-act': `interface ReflectActData {
  title: string;
  subtitle: string;
  stimulus?: { text: string; source?: string };  // Optional anchor
  // Fixed 3-stage tuple — DO NOT change the ids.
  stages: [
    {
      id: 'what';
      label: string;            // e.g. "What happened"
      description: string;      // What the learner does at this stage
      prompts: Array<{
        id: string;
        text: string;           // The prompt question
        inputType: 'text' | 'select';
        options?: string[];     // required when inputType === 'select'
        maxLength?: number;     // default 400
        placeholder?: string;
      }>;                       // 1-3 prompts
    },
    { id: 'so-what'; label: string; description: string; prompts: Array<{ id: string; text: string; inputType: 'text' | 'select'; options?: string[]; maxLength?: number; placeholder?: string }> },
    { id: 'now-what'; label: string; description: string; prompts: Array<{ id: string; text: string; inputType: 'text' | 'select'; options?: string[]; maxLength?: number; placeholder?: string }> }
  ];
  summary: {
    showFullReflection: boolean;  // Show all answers at the end
    exportEnabled: boolean;       // Allow copy-to-clipboard
    closingMessage?: string;
  };
}
// CRITICAL: stage 1 ("what") uses OBSERVATIONAL language only — no "why".
// Stage 3 ("now-what") demands SPECIFIC committed actions with constraints
// (who, when, how). Don't blur the stages.`,

  'hotspot-explorer': `interface HotspotExplorerData {
  title: string;
  subtitle: string;
  imageUrl: string;            // hosted image/diagram URL the hotspots sit on
  imageAlt: string;            // REQUIRED for a11y — describe what the image shows
  imageCaption?: string;       // optional caption rendered under the image
  hotspots: Array<{
    id: string;
    x: number;                 // 0–100 — % of image WIDTH
    y: number;                 // 0–100 — % of image HEIGHT
    label: string;             // short marker label (e.g. "Cooling tower")
    title: string;             // detail-panel heading
    body: string;              // detail-panel body — 1–3 short paragraphs
    contentType?: 'text' | 'data' | 'quote';
  }>;                          // 3-8 hotspots
  synthesis: {
    title: string;
    body: string;              // 2–3 sentences tying the hotspots together
    reflectionPrompt: string;  // answered after the synthesis unlocks
  };
}
// CRITICAL: needs a real image with discoverable spatial structure. Every x/y
// MUST be within 0–100. At least 3 hotspots. imageAlt is required. Each hotspot
// body must be specific to its zone — not identical-length boilerplate.`,
};

// ---------------------------------------------------------------------------
// Type-specific Builder guardrails. Reveal-predict has none (the contract +
// charter cover it); kept as an empty entry so the lookup is uniform.
// Extended one entry per template as Phase 4 ports them.
// ---------------------------------------------------------------------------
export const TYPE_GUARDRAILS: Record<string, string> = {
  'before-after-bridge':
    'Before prompts must be answerable WITHOUT specialised knowledge. Content blocks must include concrete, assumption-challenging data — not generic background. Prompt ids MUST match between before and after phases; the after-phase reword should make the same question feel different after the content.',
  'reflect-act':
    'Stage 1 (what) uses observational language only — no "why" or interpretive verbs. Stage 3 (now-what) must demand specific actionable commitments with constraints (who, when, how). Don\'t blur stages.',
  'spot-the-error':
    'Each "error" chunk must be a substantive factual or logical mistake, with an explanation that names WHAT is wrong and WHY (no tautologies). Decoys must be plausible-sounding but actually correct — they test discrimination. Mix text/error/decoy chunks so the errors are embedded, not bullet-listed.',
  'claim-evidence':
    'Strength ratings must be defensible. Expert reasoning must connect evidence to claim explicitly. Include at least 2 distractors. Evidence detail max 120 chars.',
  'gallery-walk':
    'Stations must be independent — each makes sense in isolation. Reflection prompts must be station-specific (no interchangeable "What did you learn?" boilerplate). Synthesis prompt asks the cross-station meta-question.',
  'perspective-lenses':
    'Each lens sub-prompt must be genuinely different — same answer across lenses defeats the point. Stimulus must include names, numbers, stakes. iconKey must be an SVG icon key from the registered library, NOT emoji.',
  'scaffolded-investigation':
    'Modes must escalate (guided → semi-guided → independent). Round 1 has presets only, round 3 has none. Each round\'s content is distinct material, not the same passage probed differently. Custom-probe count scales: 0 → 1-2 → 3-5.',
  'layered-reveal':
    'Each layer must shift the learner\'s assessment. Don\'t reuse the same assessment prompt across layers — the new context demands a new judgement. Layer 1 creates the initial impression; the synthesis reveal explains why the layers add up to a different picture.',
  'timeline-navigator':
    'Events form a cause/effect arc, not just a list. The detail field should explain WHY this event mattered, not just WHAT happened. Mark at least 2 events as milestones to anchor the arc.',
  'progressive-builder':
    'transformedOutput is CUMULATIVE — full picture after this layer. Each layer\'s output should visibly differ from the prior layer\'s. Don\'t just list additions; show the evolving result.',
  'guided-sandbox':
    'Keywords are the scoring signal — pick concrete, specific phrases a learner would use if their answer landed the dimension. Generic keywords ("good", "correct") flatten scoring. Tips should coach toward what the dimension actually wants.',
  'drag-sort':
    'Each item.correctZone must reference an existing zone id. Mix in some ambiguous-looking items that test understanding — not just trivially obvious sorts.',
  'teach-back':
    'The student must be wrong because of ONE authored belief, and the distractor diagnoses must be plausible (e.g. "can\'t do the calculation" when the work proves they can). Coaching fragments carry real consequences: the backfire fragment teaches a second wrong belief, the answer-giving fragment breeds dependency — both branch to authentic, non-cartoonish student replies. Every branch terminates in a transformed rewrite or an honest unresolved ending; an unresolved outcome is legitimate, never a dead end. The revision must smuggle exactly one residue of the original belief.',
  'tension-mapper':
    'Every factor must genuinely sit somewhere on the spectrum — none should trivially go to one pole. Factor descriptions max 80 chars. Expert insights must reference factor ids.',
  'scenario-simulation':
    'First branch MUST have id "start" and type "decision". 3-5 decision branches + 2-3 endings. Every choice.nextBranch must reference a real branch. At least one ending reachable from every choice path.',
  'hotspot-explorer':
    'Requires a genuine image/diagram with spatial structure to map zones onto. 3–8 hotspots, each with x and y within 0–100. imageAlt is mandatory and must describe the image for screen-reader parity. Each hotspot body is specific to its zone (no identical-length boilerplate); the synthesis only makes sense once every zone has been explored.',
};

export function buildBuilderSystem(
  interactionType: string,
  narrativeWrapper: boolean,
): string {
  const contract = DATA_CONTRACTS[interactionType];
  if (!contract) {
    throw new Error(
      `No data contract registered for "${interactionType}". Port its template entry to DATA_CONTRACTS before generating.`,
    );
  }

  const contractSection = `## Output Schema

The tool input you emit MUST match this TypeScript interface at the TOP LEVEL — the keys below are the keys of the tool input itself, not nested under any wrapper field:

\`\`\`typescript
${contract}
\`\`\``;

  const guardrail = TYPE_GUARDRAILS[interactionType];
  const guardrailSection = guardrail
    ? `\n## Type-Specific Guardrails (${interactionType})\n\n${guardrail}`
    : '';

  const narrativeSection = narrativeWrapper
    ? `\n\n---\n\n${NARRATIVE_WRAPPER}\n\nApply the narrative wrapper to the data object:
- The \`title\` and \`subtitle\` should establish the story context
- Weave the learner's role and scenario into content fields (questions, context, prompts)
- Use narrative language in feedback messages matching the story tone`
    : '';

  // The 6 Experience Design Rules are NOT included here: they are design
  // decisions already made by the upstream experience-designer spec stage
  // (entry frame, feedback-that-teaches, closing reflection, etc.). The
  // Builder only fills the spec into the data contract — re-teaching it the
  // rules just dilutes the prompt. (designer-spec covers rules 1/2/3/5/6;
  // rule 4 is an upstream interaction-type concern.)
  return `${CHARTER}

---

${BUILDER_SYSTEM}

---

${contractSection}
${guardrailSection}${narrativeSection}`;
}

export function buildBuilderUserMessage(input: BuilderInput): string {
  const interactionType = input.experienceSpec.interactionType;
  let qaContext = '';
  if (input.qaFeedback) {
    const issues = input.qaFeedback.criticalIssues
      .map((i) => `- [${i.location}] ${i.issue}\n  Fix: ${i.fixInstruction}`)
      .join('\n');
    qaContext = `

**QA FEEDBACK — FIX THESE ISSUES:**
The previous build failed QA review. Address each issue while preserving everything else.

${issues}`;
  }

  // Item E — design-only prior-context callbacks. Surfaces earlier built
  // slots so the Builder can weave structural references into feedback and
  // the closing reflection. Hard rule: reference what those slots ASKED,
  // never what the learner ANSWERED (that's Attune's territory).
  let priorContext = '';
  if (input.priorContext && input.priorContext.length > 0) {
    const items = input.priorContext
      .map((p) => `- Slot ${p.slotIndex} (${p.builtType}${p.position ? ` · ${p.position}` : ''}): "${p.title}" — ${p.askedAbout}`)
      .join('\n');
    priorContext = `

**Prior Interactive slots in this lesson (design-only — what they ASKED, never what the learner ANSWERED):**
${items}

When natural, weave a STRUCTURAL callback to one of these into either a feedback message or the closing reflection. The callback must reference what the prior slot ASKED the learner to think about ("Earlier you weighed the budget trade-off; now…") — NEVER refer to a specific answer or response from the learner. You do not know what the learner picked or wrote; do not invent it.`;
  }

  return `Build the SlideData for a "${interactionType}" experience from this specification.

**Experience Spec:**
${JSON.stringify(input.experienceSpec, null, 2)}
${qaContext}${priorContext}

Call the \`build_slide\` tool exactly once. The tool input MUST be the SlideData object directly — its top-level keys are the fields defined in the contract above (e.g. \`title\`, \`subtitle\`, \`predictions\`, \`summary\`).

CRITICAL — DO NOT:
- wrap the object under a \`data\` key (e.g. \`{ data: { title: ... } }\` is wrong)
- pass the entire object as a JSON string
- nest the SlideData inside any other key
- emit ANY field value as a JSON-encoded string. Arrays MUST be real JSON arrays in the tool input, not strings that *look* like arrays. E.g. \`"predictions": [{"id":...}, {"id":...}]\` is correct; \`"predictions": "[{\\"id\\":...}]"\` is WRONG. Same rule applies to \`layers\`, \`branches\`, \`stages\`, \`options\`, and every other array or object field in the contract.

The first level of the tool input IS the SlideData shape, and each field's value uses its native JSON type (array, object, number, string, boolean) — never a string-encoding of another type.`;
}
